import Home from './Home';
import Pulse from './Pulse';
import Visitors from './Visitors';
import Create from './Create';
import ContentWorkspace from './ContentWorkspace';
import UpryzzeAi from './UpryzzeAi';
import RyzzeCast from './RyzzeCast';
import SiteAlerts from './SiteAlerts';
import MediaLibraryPage from './MediaLibrary';
import LeadForm from './LeadForm';
import UavsScore from './UavsScore';
import HelpPage from '../components/help/HelpPage';
import Profile from './Profile';
import Inbox from './Inbox';
import InboxConversation from './InboxConversation';
import Crm from './Crm';
import CrmCustomer from './CrmCustomer';
import Quotes from './Quotes';
import QuoteDetail from './QuoteDetail';
import QuoteEditor from './QuoteEditor';
import Invoices from './Invoices';
import InvoiceDetail from './InvoiceDetail';
import InvoiceEditor from './InvoiceEditor';
import StripeConnectSettings from './StripeConnectSettings';
import Payments from './Payments';
import BillingSettings from './BillingSettings';
import AiUsage from './AiUsage';
import Settings from './Settings';
import { QuoteDrafts, InvoiceDrafts, WorkflowFormDrafts } from './OutboxDrafts';
import WorkflowForms from './WorkflowForms';
import WorkflowFormFill from './WorkflowFormFill';
import WorkflowFormSubmissionDetail from './WorkflowFormSubmissionDetail';
import WorkflowFormAuthor from './WorkflowFormAuthor';
import BlogAuthor from './BlogAuthor';
import BuildInterview from './BuildInterview';
import BuildDraftReview from './BuildDraftReview';
import GoLiveChecklist from './GoLiveChecklist';
import WebsiteBuild from './WebsiteBuild';
import Scheduling from './Scheduling';

// Pages with `hideFromNav: true` are routable but don't render in the
// header navigation. Detail pages reached only by drill-down from a list
// (inbox conversation, CRM customer, quote detail) carry that flag.
//
// AI Search was removed alongside the same cleanup in the tools console
// (PR 6b in supportupryzze/upryzze-tools). The features it surfaced
// (citation directory tracking, review campaigns, per-query AI mention
// narrative) either don't pay rent at our scale or are being folded
// into the UAVS audit detail view ("AI Visibility" / UavsScore page).
//
// Quote editor (2.14) + detail (2.15) shipped — placeholders removed.
// All quote + invoice routes now bind to real pages.
export const Workspace_Pages = [
  { key: 'home', path: '', navLabel: 'Home', element: <Home /> },
  { key: 'pulse', path: 'pulse', navLabel: 'Pulse', element: <Pulse /> },
  // Site analytics deep-dive. Sits between Pulse (the glance) and Inbox
  // (the action queue) — natural funnel ordering: visit → conversation →
  // CRM record. See docs/design/site-analytics.md.
  {
    key: 'visitors',
    path: 'visitors',
    navLabel: 'Analytics',
    element: <Visitors />,
  },
  { key: 'inbox', path: 'inbox', navLabel: 'Inbox', element: <Inbox /> },
  // "Create" hub — mobile bottom-tab proactive-creation launcher. Routable
  // but hidden from the desktop sidebar (it's a mobile concept; desktop uses
  // the per-area new buttons). See docs/design/mobile-console-ux.md §5.5.
  {
    key: 'create',
    path: 'create',
    element: <Create />,
    hideFromNav: true,
  },
  {
    key: 'inbox-conversation',
    path: 'inbox/:id',
    element: <InboxConversation />,
    hideFromNav: true,
  },
  { key: 'crm', path: 'crm', navLabel: 'CRM', element: <Crm /> },
  {
    key: 'crm-customer',
    path: 'crm/:id',
    element: <CrmCustomer />,
    hideFromNav: true,
  },
  { key: 'quotes', path: 'quotes', navLabel: 'Estimates', element: <Quotes /> },
  {
    key: 'quotes-drafts',
    path: 'quotes/drafts',
    element: <QuoteDrafts />,
    hideFromNav: true,
  },
  {
    key: 'quotes-new',
    path: 'quotes/new',
    element: <QuoteEditor />,
    hideFromNav: true,
  },
  {
    key: 'quote-detail',
    path: 'quotes/:id',
    element: <QuoteDetail />,
    hideFromNav: true,
  },
  {
    key: 'quote-edit',
    path: 'quotes/:id/edit',
    element: <QuoteEditor />,
    hideFromNav: true,
  },
  {
    key: 'invoices',
    path: 'invoices',
    navLabel: 'Invoices',
    element: <Invoices />,
  },
  {
    key: 'invoices-drafts',
    path: 'invoices/drafts',
    element: <InvoiceDrafts />,
    hideFromNav: true,
  },
  {
    key: 'invoices-new',
    path: 'invoices/new',
    element: <InvoiceEditor />,
    hideFromNav: true,
  },
  {
    key: 'invoice-detail',
    path: 'invoices/:id',
    element: <InvoiceDetail />,
    hideFromNav: true,
  },
  {
    key: 'invoice-edit',
    path: 'invoices/:id/edit',
    element: <InvoiceEditor />,
    hideFromNav: true,
  },
  // Payments dashboard (design handoff Page 13) — Stripe Connect money
  // rails: balance, payouts, recent payments with fee + net. Sits after
  // Invoices in the money spine (quote → invoice → payment). The
  // onboarding/management surface stays on `stripe-connect`
  // ("Payment settings"), linked from this page's header.
  {
    key: 'payments',
    path: 'payments',
    navLabel: 'Payments',
    element: <Payments />,
  },
  // 4.11 — top-level Workflow Forms list page (Console-auth via the
  // 4.6 endpoint).
  {
    key: 'forms',
    path: 'forms',
    navLabel: 'Forms',
    element: <WorkflowForms />,
  },
  // Scheduling (D35 / doc 20 Phase A). One nav entry landing on a tabbed
  // page (Calendar / Approvals / Availability / Settings). Self-gates on the
  // `scheduling` tenant feature (GET /v2/features): routed + listed for all,
  // with a friendly "not turned on" state for tenants without it, matching
  // the cms-ai / RyzzeCast pattern.
  {
    key: 'scheduling',
    path: 'scheduling',
    navLabel: 'Scheduling',
    element: <Scheduling />,
  },
  // AI-guided blog authoring (design: docs/design/ai-blog-authoring.md).
  // Client self-serve: chat -> draft to uat -> preview -> approve and
  // publish. Author-role gated server-side; non-admins get a clear
  // message.
  //
  // Now surfaced as the "AI Writer" tab inside the Content workspace
  // (ContentWorkspace, plan 3.1.4) to kill the nav fragmentation. This
  // standalone route is KEPT for deep links / existing bookmarks but
  // hidden from the nav; `/content?tab=ai` is the canonical entry.
  {
    key: 'blog-ai',
    path: 'blog/ai-writer',
    navLabel: 'AI Blog Writer',
    element: <BlogAuthor />,
    hideFromNav: true,
  },
  // 5.10 — tenant self-serve AI form authoring (chat → preview → submit
  // for staff approval). businessadmin-gated server-side; non-admins get
  // a clear message. Visible in nav so it's discoverable.
  {
    key: 'forms-ai-builder',
    path: 'forms/ai-builder',
    navLabel: 'AI Form Builder',
    element: <WorkflowFormAuthor />,
  },
  // Customer-facing re-entry hub for the build funnel. Customers start
  // their build on the marketing site; this is the single nav-visible door
  // back into it (the three step pages below stay hideFromNav). Self-gates
  // on GET /v2/build/status (degrade-opens to 'none'), so it is safe to
  // route + list for all: a tenant with no build sees a friendly empty
  // state pointing back to the funnel.
  {
    key: 'website-build',
    path: 'website-build',
    navLabel: 'Website Build',
    element: <WebsiteBuild />,
  },
  // 8.7 - the S2 build interview (AI site generator). Routable now,
  // effectively dormant until 8.4 creates BuildJobs (the page shows a
  // friendly not-ready state when no build exists). Hidden from nav
  // until the funnel is live end to end.
  {
    key: 'build-interview',
    path: 'build',
    element: <BuildInterview />,
    hideFromNav: true,
  },
  // 8.10 - draft review + approval (Approve gate v1). After generation
  // deploys a draft, the tenant reviews it here: open the preview, then
  // approve (-> approved_pending_review, the founder's delivery/charge
  // gate) or request changes with a note. Reached from the build flow /
  // a notification; hidden from nav until the funnel is live end to end.
  {
    key: 'build-review',
    path: 'build/review',
    element: <BuildDraftReview />,
    hideFromNav: true,
  },
  // S7 - go-live checklist. After the founder clears the build
  // (approved_for_golive), the customer connects their domain here: add the
  // rendered DNS records, then confirm to publish. Reached from the build
  // flow / a notification; hidden from nav until the funnel is live end to end.
  {
    key: 'build-go-live',
    path: 'build/go-live',
    element: <GoLiveChecklist />,
    hideFromNav: true,
  },
  // 5.2 — offline drafts for workflow-form submissions.
  {
    key: 'forms-drafts',
    path: 'forms/drafts',
    element: <WorkflowFormDrafts />,
    hideFromNav: true,
  },
  // 4.12 — Console-side renderer. Consumes the compiled schema from
  // 4.6's GET, walks the flat `fields[]`, and submits via 4.7 with
  // the 5.2 offline outbox wired in.
  {
    key: 'forms-fill',
    path: 'forms/:slug/new',
    element: <WorkflowFormFill />,
    hideFromNav: true,
  },
  // 4.11b — submission detail view. Reached from the customer
  // profile Forms tab (or from a future tenant-wide submissions
  // list).
  {
    key: 'form-submission-detail',
    path: 'forms/submissions/:id',
    element: <WorkflowFormSubmissionDetail />,
    hideFromNav: true,
  },
  {
    key: 'content',
    path: 'content',
    navLabel: 'Content',
    element: <ContentWorkspace />,
  },
  // UpRyzze AI: conversational CMS (docs/design/upryzze-ai-cms.md). The page
  // self-gates on GET /cms-ai/status, so it is safe to route + list for all;
  // non-allowlisted tenants see a friendly "not available" state.
  {
    key: 'cms-ai',
    path: 'cms-ai',
    navLabel: 'UpRyzze AI',
    element: <UpryzzeAi />,
  },
  // RyzzeCast: compose a post once and route it to your website and connected
  // channels (content syndication). The page self-gates on
  // GET /v2/ryzzecast/status (the content_syndication feature), so it is safe
  // to route + list for all; tenants without the feature see a friendly "not
  // available" state. See docs/design/content-syndication-hub.md.
  {
    key: 'ryzzecast',
    path: 'ryzzecast',
    navLabel: 'RyzzeCast',
    element: <RyzzeCast />,
  },
  // Emergency alert banner (emergency_alerts tenant feature). Self-gates on
  // GET /v2/site-alerts/status; the sidebar lists it only for tenants with the
  // feature on (nav-config `feature`).
  {
    key: 'site-alerts',
    path: 'site-alerts',
    navLabel: 'Site alerts',
    element: <SiteAlerts />,
  },
  {
    key: 'media',
    path: 'media',
    navLabel: 'Media',
    element: <MediaLibraryPage />,
  },
  // Public lead-capture (contact/estimate) form: read-only insights +
  // funnel. Submissions live in the Inbox; config in the Tools admin
  // surface (D24). Distinct from the internal workflow-forms `/forms`.
  {
    key: 'lead-form',
    path: 'lead-form',
    navLabel: 'Lead form',
    element: <LeadForm />,
  },
  {
    key: 'uavs',
    path: 'uavs',
    navLabel: 'AI Visibility',
    element: <UavsScore />,
  },
  { key: 'help', path: 'help', navLabel: 'Help', element: <HelpPage /> },
  // Settings hub: one nav entry that consolidates the Account-group pages
  // (Profile, Payment settings, Billing, AI usage) behind an Account/
  // Workspace sub-nav. The individual routes below are KEPT (deep links +
  // the Payments page's "Payout settings" link), so this is additive.
  {
    key: 'settings',
    path: 'settings',
    navLabel: 'Settings',
    element: <Settings />,
  },
  {
    key: 'profile',
    path: 'profile',
    navLabel: 'Profile',
    element: <Profile />,
  },
  // Relabeled from "Payments" when the `payments` dashboard page landed —
  // this is the Stripe Connect onboarding + bank/payout management
  // surface. Route path stays `stripe-connect` (deep links + PWA
  // shortcuts keep working).
  {
    key: 'stripe-connect',
    path: 'stripe-connect',
    navLabel: 'Payment settings',
    element: <StripeConnectSettings />,
  },
  {
    key: 'billing-settings',
    path: 'billing-settings',
    navLabel: 'Billing',
    element: <BillingSettings />,
  },
  // Tenant AI usage visibility — spend vs monthly cap, reset date, token
  // tally, per-api breakdown (GET /api/v2/ai-usage).
  {
    key: 'ai-usage',
    path: 'ai-usage',
    navLabel: 'AI Usage',
    element: <AiUsage />,
  },
];
